import { hasPermission } from '@crm/shared'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { Bell, BellOff, Check, Eye, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge, Card, Skeleton } from '@/components/ui/field'
import { cn } from '@/lib/utils'
import {
  alertsQuery,
  unreadAlertCountQuery,
  useDismissAlert,
  useMarkAlertRead,
  type AlertItem,
} from '@/features/alerts/api'
import { fetchMe } from '@/lib/auth-query'
import { formatDateTime } from '@/lib/utils'
import { useState } from 'react'

export const Route = createFileRoute('/_app/alerts')({
  beforeLoad: async ({ context }) => {
    const me = await fetchMe(context.queryClient)
    if (me && !hasPermission(me.user.role, 'alerts:view')) {
      throw redirect({ to: '/' })
    }
  },
  component: AlertsPage,
})

import type { BadgeVariant } from '@/components/ui/field'

const SEVERITY_TONE: Record<string, BadgeVariant> = {
  critical: 'destructive',
  warning: 'warning',
  info: 'muted',
}

function AlertsPage() {
  const [showRead, setShowRead] = useState(false)
  const { data, isLoading } = useQuery(alertsQuery({ includeRead: showRead }))
  const markRead = useMarkAlertRead()
  const dismiss = useDismissAlert()

  const alerts = data?.items ?? []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Alerts</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            {data?.unreadCount ?? 0} unread
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={showRead}
            onChange={(e) => setShowRead(e.target.checked)}
          />
          Show read &amp; dismissed
        </label>
      </div>

      {isLoading && (
        <div className="space-y-2 p-8">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-3/4" />
        </div>
      )}

      {!isLoading && alerts.length === 0 && (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <BellOff className="h-8 w-8 text-[var(--color-muted-foreground)]" />
          <p className="text-sm text-[var(--color-muted-foreground)]">No alerts</p>
        </Card>
      )}

      <div className="space-y-2">
        {alerts.map((alert) => (
          <AlertCard
            key={alert.id}
            alert={alert}
            onRead={() => markRead.mutate(alert.id)}
            onDismiss={() => dismiss.mutate(alert.id)}
          />
        ))}
      </div>
    </div>
  )
}

function AlertCard({
  alert,
  onRead,
  onDismiss,
}: {
  alert: AlertItem
  onRead: () => void
  onDismiss: () => void
}) {
  const isRead = !!alert.readAt

  return (
    <Card
      className={cn(
        'p-4 transition-all duration-150 ease-smooth',
        !isRead && 'border-l-2 border-l-[var(--color-primary)] bg-[var(--color-primary-muted)]/30',
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={SEVERITY_TONE[alert.severity] ?? 'muted'}>{alert.severity}</Badge>
            <span className={cn('text-sm', !isRead && 'font-semibold')}>{alert.title}</span>
            {!isRead && <span className="h-2 w-2 rounded-full bg-[var(--color-primary)]" />}
          </div>
          {alert.description && (
            <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{alert.description}</p>
          )}
          {alert.clientName && (
            <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
              Client: {alert.clientName}
            </p>
          )}
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {formatDateTime(alert.createdAt)}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-0.5">
          {alert.linkTo && (
            <Link to={alert.linkTo as any}>
              <Button variant="ghost" size="sm" aria-label="View related">
                <Eye className="h-3.5 w-3.5" />
              </Button>
            </Link>
          )}
          {!isRead && (
            <Button variant="ghost" size="sm" onClick={onRead} aria-label="Mark as read">
              <Check className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onDismiss} aria-label="Dismiss">
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </Card>
  )
}
